
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { getAdvancedInventoryItems } from '@/services/inventory/inventoryService';

interface InventoryItem {
  id: string;
  name: string;
  description?: string;
  current_quantity: number;
  minimum_quantity: number;
}

interface InventoryChartProps {
  storeId: string;
  isLoading: boolean;
  inventory: InventoryItem[];
}

export function InventoryChart({ storeId, isLoading, inventory }: InventoryChartProps) {
  // Process inventory data for the chart
  const getInventoryByCategory = () => {
    // Group items by category (using description field as category for demo)
    const categoryMap = new Map();
    
    inventory.forEach(item => {
      const category = item.description || 'Uncategorized';
      if (!categoryMap.has(category)) {
        categoryMap.set(category, {
          name: category,
          current: 0,
          minimum: 0,
          count: 0
        });
      }
      
      const categoryData = categoryMap.get(category);
      categoryData.current += item.current_quantity;
      categoryData.minimum += item.minimum_quantity;
      categoryData.count += 1;
    });
    
    // Convert map to array and sort by current quantity
    return Array.from(categoryMap.values())
      .sort((a, b) => b.current - a.current)
      .slice(0, 6); // Show top 6 categories
  };

  const chartData = getInventoryByCategory();
  
  // Get top categories by number of items
  const topCategories = Array.from(new Set(inventory.map(item => item.description || 'Uncategorized')))
    .slice(0, 5);

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">Advanced Inventory by Category</h3>
      
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : inventory.length === 0 ? (
        <div className="h-40 flex items-center justify-center">
          <p className="text-muted-foreground">No advanced inventory data available</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1 mb-3">
            {topCategories.map(category => (
              <Badge key={category} variant="outline" className="text-xs">
                {category}
              </Badge>
            ))}
          </div>
          
          <div className="h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} tickMargin={5} />
                <YAxis fontSize={12} />
                <Tooltip 
                  formatter={(value, name) => [value, name === 'current' ? 'Current Stock' : 'Minimum Required']}
                  labelFormatter={(label) => `Category: ${label}`}
                />
                <Legend />
                <Bar dataKey="current" name="Current Stock" fill="#6366F1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="minimum" name="Minimum Required" fill="#F43F5E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </Card>
  );
}
